import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { SSOButtons } from '@/components/SSOButtons';
import { Banner } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { ApiError, api, apiErrorCode } from '@/lib/api';
import { cn } from '@/lib/classnames';
import { safeReturn, ssoErrorMessage } from '@/lib/signIn';

// LoginPage - the sign-in page. /login offers each organization's single
// sign-on above the password form, and /login/local is the password form
// alone, the way in for a break-glass account when an identity provider is
// down. Where a signed-in person goes next is SignedInRedirect's business.
export default function LoginPage({ local = false }: { local?: boolean }) {
  const { login } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const returnPath = safeReturn(params.get('return'));
  const error = params.get('error');

  const offeredQ = useQuery({ queryKey: ['sso-offered'], queryFn: () => api.ssoOffered(), enabled: !local });
  const offered = local ? [] : (offeredQ.data ?? []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ssoRequired, setSSORequired] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSSORequired(false);
    try {
      await login(email, password);
    } catch (err) {
      if (apiErrorCode(err) === 'sso_required') setSSORequired(true);
      else if (err instanceof ApiError && err.kind === 'rate_limited') toast.error('Too many attempts. Wait a minute.');
      else if (err instanceof ApiError) toast.error(err.message || 'Sign-in failed. Try again.');
      else toast.error('Sign-in failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const others = local ? `/login?return=${encodeURIComponent(returnPath)}` : null;

  return (
    <AuthShell subtitle="Precision malware analysis">
      <div className="card p-8">
        <div className="mb-6 text-center">
          <h1 className="text-title font-semibold">Sign in</h1>
          {local && <p className="text-prose text-ink-400 mt-1">Use your email and password.</p>}
        </div>

        <div className="flex flex-col gap-5">
          {error && <Banner variant="danger">{ssoErrorMessage(error)}</Banner>}
          {ssoRequired && <Banner>Your organization signs in with single sign-on.</Banner>}

          {offered.length > 0 && (
            <>
              <SSOButtons offered={offered} returnPath={returnPath} />
              <div className="flex items-center gap-3 text-mini text-ink-400" aria-hidden>
                <span className="h-px flex-1 bg-rule" />
                or
                <span className="h-px flex-1 bg-rule" />
              </div>
            </>
          )}

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <label className="block">
              <span className="label">Email</span>
              <input
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label">Password</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <button
              type="submit"
              className={cn(offered.length > 0 ? 'btn-secondary' : 'btn-primary', 'w-full justify-center mt-2')}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-label="Signing in" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </>
              )}
            </button>
          </form>

          {others && (
            <Link to={others} className="btn-ghost w-full justify-center">
              Other ways to sign in
            </Link>
          )}
        </div>
      </div>

      <p className="text-center text-mini text-ink-400 mt-6">
        Foresee is invite-only. Contact your administrator if you don't have an account.
      </p>
    </AuthShell>
  );
}
